import {
  approveDocumentRequestMutation,
  changeDocumentTypeMutation,
  getDocumentsToReviewQuery,
  getRequestListReadyToReviewQuery,
  IRequest,
  markDriverLicenseAsWithoutPhotoMutation,
  purgeDocumentRequestMutation,
  rejectDocumentRequestMutation,
  requestMutation,
  SaveDocumentDetailsRequest,
  SaveDocumentDetailsResponse,
  sendDriverRequestSmsMutation,
  sendDriverRequestSmsToNewNumberMutation,
  updateDocumentDetailsDocumentDotMedicalMutation,
  updateDocumentFileMutation,
} from './interfaces';
import {
  apiService,
  candidateDocumentsListTag,
  documentReviewTag,
  driverDocumentsTag,
  organizationTag,
} from '../api';

const requestService = apiService.injectEndpoints({
  endpoints: (builder) => ({
    createRequest: builder.mutation<
      requestMutation['response'],
      requestMutation['request']
    >({
      async queryFn({ data }, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `/document-request/manager/newDocumentRequest`,
          method: 'POST',
          body: data,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as IRequest };
      },
      invalidatesTags: [{ type: documentReviewTag }],
    }),
    changeDocumentType: builder.mutation<
      changeDocumentTypeMutation['response'],
      changeDocumentTypeMutation['request']
    >({
      async queryFn(
        { documentId, documentType },
        _api,
        _extraOptions,
        baseQuery
      ) {
        const result = await baseQuery({
          url: `/document-request/manager/change-document-type`,
          method: 'POST',
          body: { documentId, documentType },
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as changeDocumentTypeMutation['response'] };
      },
      invalidatesTags: [
        { type: documentReviewTag },
        { type: candidateDocumentsListTag },
      ],
    }),
    sendDriverRequestSms: builder.mutation<
      sendDriverRequestSmsMutation['response'],
      sendDriverRequestSmsMutation['request']
    >({
      async queryFn(
        { data, documentRequestId },
        _api,
        _extraOptions,
        baseQuery
      ) {
        const result = await baseQuery({
          url: `/document-request/manager/apply-document-request-to-driver/${documentRequestId}`,
          method: 'POST',
          body: data,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as IRequest };
      },
    }),
    sendDriverRequestSmsToNewNumber: builder.mutation<
      sendDriverRequestSmsToNewNumberMutation['response'],
      sendDriverRequestSmsToNewNumberMutation['request']
    >({
      async queryFn(
        { data, documentRequestId },
        _api,
        _extraOptions,
        baseQuery
      ) {
        const result = await baseQuery({
          url: `/document-request/manager/apply-document-request-to-driver-new-number/${documentRequestId}`,
          method: 'POST',
          body: data,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as IRequest };
      },
    }),
    getRequestReadyToReviewList: builder.query<
      getRequestListReadyToReviewQuery['response'],
      getRequestListReadyToReviewQuery['request']
    >({
      query: ({ query }) => ({
        url: `/document-request/manager/document-request-list-ready-for-review/${
          query ? `?${query}` : ''
        }`,
        method: 'GET',
      }),
      providesTags: [{ type: organizationTag }, { type: documentReviewTag }],
    }),
    getMyRequestList: builder.query<
      getRequestListReadyToReviewQuery['response'],
      getRequestListReadyToReviewQuery['request']
    >({
      query: ({ query }) => ({
        url: `/document-request/manager/document-request-list-awaiting-documents/${
          query ? `?${query}` : ''
        }`,
        method: 'GET',
      }),
      providesTags: [{ type: organizationTag }, { type: documentReviewTag }],
    }),
    getDocumentsToReview: builder.query<
      getDocumentsToReviewQuery['response'],
      getDocumentsToReviewQuery['request']
    >({
      query: ({ documentRequestId }) => ({
        url: `/document-request/manager/documents-to-review/${documentRequestId}`,
        method: 'GET',
      }),
      providesTags: [{ type: organizationTag }, { type: documentReviewTag }],
    }),
    approveDocumentRequest: builder.mutation<
      approveDocumentRequestMutation['response'],
      approveDocumentRequestMutation['request']
    >({
      query: ({ documentRequestId, candidateDocumentId, details }) => ({
        url: `/document-request/manager/approve-document/${documentRequestId}/${candidateDocumentId}`,
        method: 'POST',
        body: { details },
      }),
      invalidatesTags: [
        { type: documentReviewTag },
        { type: candidateDocumentsListTag },
      ],
    }),
    rejectDocumentRequest: builder.mutation<
      rejectDocumentRequestMutation['response'],
      rejectDocumentRequestMutation['request']
    >({
      query: ({
        documentRequestId,
        candidateDocumentId,
        rejectionDetails,
        rejectionReason,
        removeDocumentFromRequest,
      }) => ({
        url: `/document-request/manager/reject-document/${documentRequestId}/${candidateDocumentId}`,
        method: 'POST',
        body: {
          documentRequestId,
          candidateDocumentId,
          rejectionDetails,
          rejectionReason,
          removeDocumentFromRequest,
        },
      }),
      invalidatesTags: [
        { type: documentReviewTag },
        { type: candidateDocumentsListTag },
      ],
    }),
    purgeDocumentRequest: builder.mutation<
      purgeDocumentRequestMutation['response'],
      purgeDocumentRequestMutation['request']
    >({
      query: ({ documentRequestId, candidateDocumentId }) => ({
        url: `/document-request/manager/candidate-document/${documentRequestId}/${candidateDocumentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: documentReviewTag },
        { type: candidateDocumentsListTag },
      ],
    }),
    purgeDocumentForever: builder.mutation<
      purgeDocumentRequestMutation['response'],
      purgeDocumentRequestMutation['request']
    >({
      query: ({ documentRequestId, candidateDocumentId }) => ({
        url: `/document-request/manager/candidate-document/remove/${documentRequestId}/${candidateDocumentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: documentReviewTag },
        { type: candidateDocumentsListTag },
      ],
    }),
    updateDocumentDetailsDocumentDotMedical: builder.mutation<
      updateDocumentDetailsDocumentDotMedicalMutation['response'],
      updateDocumentDetailsDocumentDotMedicalMutation['request']
    >({
      query: ({ candidateDocumentId, details }) => ({
        url: `/document-request/manager/document-details/dot-medical/${candidateDocumentId}`,
        method: 'PUT',
        body: { ...details },
      }),
      invalidatesTags: [
        { type: documentReviewTag },
        { type: candidateDocumentsListTag },
      ],
    }),
    updateDocumentFile: builder.mutation<
      updateDocumentFileMutation['response'],
      updateDocumentFileMutation['request']
    >({
      query: ({ candidateDocumentId, fileName, file }) => ({
        url: `document-request/manager/update-file/${candidateDocumentId}/${fileName}`,
        method: 'PUT',
        body: file,
      }),
      invalidatesTags: [
        { type: documentReviewTag },
        { type: candidateDocumentsListTag },
      ],
    }),
    saveDocumentDetails: builder.mutation<
      SaveDocumentDetailsResponse,
      SaveDocumentDetailsRequest
    >({
      async queryFn(args, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `/document-request/candidate/document-details/${args.candidateDocumentId}`,
          method: args.confirmPost ? 'POST' : 'PATCH',
          body: args.form,
        });

        if (result.error) {
          return { error: result.error };
        }
        const data = result.data as SaveDocumentDetailsResponse;
        return { data };
      },
    }),

    markDriverLicenseAsWithoutPhoto: builder.mutation<
      markDriverLicenseAsWithoutPhotoMutation['response'],
      markDriverLicenseAsWithoutPhotoMutation['request']
    >({
      async queryFn(
        { candidateDocumentId, ...body },
        _api,
        _extraOptions,
        baseQuery
      ) {
        const result = await baseQuery({
          url: `/document-request/manager/mark-driver-license-as-without-photo/${candidateDocumentId}`,
          method: 'POST',
          body: body,
        });

        if (result.error) {
          return { error: result.error };
        }
        const data =
          result.data as markDriverLicenseAsWithoutPhotoMutation['response'];
        return { data };
      },
    }),

    candidateMarkDriverLicenseAsWithoutPhoto: builder.mutation<
      markDriverLicenseAsWithoutPhotoMutation['response'],
      markDriverLicenseAsWithoutPhotoMutation['request']
    >({
      async queryFn(
        { candidateDocumentId, ...body },
        _api,
        _extraOptions,
        baseQuery
      ) {
        const result = await baseQuery({
          url: `/document-request/candidate/mark-driver-license-as-without-photo/${candidateDocumentId}`,
          method: 'POST',
          body,
        });

        if (result.error) {
          return { error: result.error };
        }
        const data =
          result.data as markDriverLicenseAsWithoutPhotoMutation['response'];
        return { data };
      },
      invalidatesTags: [driverDocumentsTag],
    }),
  }),
});

export default requestService;
